// Aba "Certificados MTLS" — o cofre de certificados do lojista.
//
// Antes do cofre, o mesmo par PEM ficava copiado em cada configuração de
// processor: um lojista Itaú com PIX + BANK_SLIP + BOLEPIX guardava 3 cópias, e
// renovar exigia repetir a operação em cada uma — esquecer uma derrubava o
// método em produção.
//
// Aqui o certificado é um recurso do lojista: substituir uma entrada atualiza
// TODAS as configurações vinculadas de uma vez. Por isso a coluna "Em uso"
// existe — ela é o alcance real de cada ação.
//
// Contrato: payment/docs/MTLS_CERTIFICATE_VAULT.md.
import {
  PlusIcon,
  RefreshCwIcon,
  Trash2Icon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CertificateStatusBadge } from "@/components/commons/certificate-status-badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { FRONTEND_ROUTES } from "@/constants"
import {
  useDeleteMerchantMtlsCertificateMutation,
  useMerchantMtlsCertificates,
  useRenewMerchantMtlsCertificateMutation,
} from "@/services/admin/merchant-mtls-certificates"
import type { MerchantMtlsCertificate } from "@/types/admin-merchant-mtls-certificate"

import { CreateMtlsCertificateDialog } from "./create-mtls-certificate-dialog"

const panelPrefix = "pages.adminMerchants"

// Sem prop de merchant: o cofre é o da loja do usuário logado, e quem resolve
// isso é o gateway a partir do token.
export const MerchantMtlsCertificatesTab = () => {
  const { t } = useTranslation()
  const { data: certificates, isLoading } =
    useMerchantMtlsCertificates()
  const deleteMutation =
    useDeleteMerchantMtlsCertificateMutation()
  const renewMutation =
    useRenewMerchantMtlsCertificateMutation()

  const [createOpen, setCreateOpen] = useState(false)
  const [pendingDelete, setPendingDelete] =
    useState<MerchantMtlsCertificate | null>(null)

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  const entries = certificates ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold tracking-tight">
            {t(`${panelPrefix}.mtlsCertificates.title`)}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t(`${panelPrefix}.mtlsCertificates.subtitle`)}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <PlusIcon className="mr-2 size-4" />
          {t(`${panelPrefix}.mtlsCertificates.add`)}
        </Button>
      </div>

      {entries.length === 0 ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t(`${panelPrefix}.mtlsCertificates.empty`)}
        </p>
      ) : (
        <ul className="space-y-3">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4"
            >
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {/* O nome leva ao detalhe: é lá que ficam a trilha e a
                      substituição, que valem para todas as configurações. */}
                  <Link
                    to={`${FRONTEND_ROUTES.ADMIN_MERCHANT.href}/certificates/${entry.id}`}
                    className="font-medium underline-offset-4 hover:underline"
                  >
                    {entry.name}
                  </Link>
                  <CertificateStatusBadge
                    status={entry.certificate.status}
                    daysToExpire={
                      entry.certificate.daysToExpire
                    }
                  />
                  {entry.gateways.map((gateway) => (
                    <Badge
                      key={gateway}
                      variant="outline"
                      className="border-violet-200 bg-violet-50 text-violet-700"
                    >
                      {gateway}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {/* O alcance da ação: quantas configurações param se este
                      certificado vencer. */}
                  {t(
                    `${panelPrefix}.mtlsCertificates.linkedConfigs`,
                    { count: entry.linkedConfigsCount }
                  )}
                  {entry.certificate.fingerprintSha256
                    ? ` · ${entry.certificate.fingerprintSha256}`
                    : ""}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t(
                    `${panelPrefix}.mtlsCertificates.renew`
                  )}
                  disabled={renewMutation.isPending}
                  onClick={() =>
                    renewMutation.mutate(entry.id)
                  }
                >
                  <RefreshCwIcon className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t(
                    `${panelPrefix}.mtlsCertificates.remove`
                  )}
                  onClick={() => setPendingDelete(entry)}
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <CreateMtlsCertificateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) =>
          !open && setPendingDelete(null)
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                `${panelPrefix}.mtlsCertificates.removeTitle`
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {/* Remover um certificado em uso deixaria as configurações sem
                  material, e elas parariam no próximo handshake — o backend
                  recusa com 409, mas o aviso vem antes do clique. */}
              {t(
                `${panelPrefix}.mtlsCertificates.removeDescription`,
                {
                  name: pendingDelete?.name ?? "",
                  count:
                    pendingDelete?.linkedConfigsCount ?? 0,
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) {
                  deleteMutation.mutate(pendingDelete.id)
                }
                setPendingDelete(null)
              }}
            >
              {t(`${panelPrefix}.mtlsCertificates.remove`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
